/**
 * Upgrade Pipeline Chart (Stacked Bar)
 *
 * Shows tenant container version breakdown: current / outdated / deprecated.
 * Data comes from containerStatus.byVersion in dashboard metrics.
 * Phase 3.5-D: Dashboard Infrastructure Stubs (STUB-DASH-009)
 */

import React from 'react';

export interface UpgradePipelineChartProps {
  byVersion: { current: number; outdated: number; deprecated: number } | null;
  isLoading: boolean;
}

const SEGMENT_CONFIG = [
  { key: 'current' as const, label: 'Current', color: 'var(--success, #22c55e)' },
  { key: 'outdated' as const, label: 'Outdated', color: 'var(--warning, #f59e0b)' },
  { key: 'deprecated' as const, label: 'Deprecated', color: 'var(--danger, #ef4444)' },
];

export const UpgradePipelineChart: React.FC<UpgradePipelineChartProps> = ({
  byVersion,
  isLoading,
}) => {
  if (isLoading && !byVersion) {
    return (
      <div style={{ height: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
        Loading...
      </div>
    );
  }

  if (!byVersion) {
    return (
      <div style={{ height: '160px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', fontSize: 'var(--text-sm)' }}>
        No data
      </div>
    );
  }

  const total = byVersion.current + byVersion.outdated + byVersion.deprecated;

  if (total === 0) {
    return (
      <div style={{ height: '160px', display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', color: 'var(--text-muted)', gap: 'var(--space-2)' }}>
        <span style={{ fontSize: 'var(--text-sm)' }}>No containers to track</span>
      </div>
    );
  }

  // All current = success state
  const allCurrent = byVersion.outdated === 0 && byVersion.deprecated === 0;

  return (
    <div style={{ height: '160px', display: 'flex', flexDirection: 'column', justifyContent: 'space-between' }}>
      {/* Stacked bar */}
      <div
        style={{
          width: '100%',
          height: 28,
          display: 'flex',
          borderRadius: 'var(--radius-sm, 4px)',
          overflow: 'hidden',
          background: 'var(--bg-secondary, #1a1a2e)',
        }}
      >
        {SEGMENT_CONFIG.map(({ key, color }) => {
          const count = byVersion[key];
          if (count === 0) return null;
          const pct = (count / total) * 100;
          return (
            <div
              key={key}
              style={{
                width: `${pct}%`,
                height: '100%',
                background: color,
                transition: 'width 0.5s ease',
                minWidth: 4,
              }}
              title={`${key}: ${count} (${Math.round(pct)}%)`}
            />
          );
        })}
      </div>

      {/* Legend */}
      <div style={{ display: 'flex', flexDirection: 'column', gap: 'var(--space-2)', marginTop: 'var(--space-3)' }}>
        {SEGMENT_CONFIG.map(({ key, label, color }) => {
          const count = byVersion[key];
          const pct = total > 0 ? Math.round((count / total) * 100) : 0;
          return (
            <div key={key} style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', fontSize: 'var(--text-sm)' }}>
              <div style={{ width: 10, height: 10, borderRadius: 2, background: color, flexShrink: 0 }} />
              <span style={{ color: 'var(--text-secondary)', flex: 1 }}>{label}</span>
              <span style={{ color: 'var(--text-primary)', fontWeight: 600, minWidth: 24, textAlign: 'right' }}>{count}</span>
              <span style={{ color: 'var(--text-muted)', fontSize: 'var(--text-xs)', minWidth: 36, textAlign: 'right' }}>({pct}%)</span>
            </div>
          );
        })}
      </div>

      {/* Status line */}
      {allCurrent && (
        <div style={{ marginTop: 'var(--space-2)', fontSize: 'var(--text-xs)', color: 'var(--success, #22c55e)', textAlign: 'center' }}>
          All containers on current version
        </div>
      )}
    </div>
  );
};

export default UpgradePipelineChart;
